import React, { useState, useEffect } from 'react';
import TogglePanel from '../components/TogglePanel';
import DeleteButton from '../components/DeleteButton';
import AccountInformationModal from '../components/AccountInformationModal';
import LoreBotChatActivityModal from '../components/LoreBotChatActivityModal';
import LoreBotConsentModal from '../components/LoreBotConsentModal';
import AETNARequiredInformationSharingConsentModal from '../components/AETNARequiredInformationSharingConsentModal';
import { userPreferencesApiClient } from '../services/userPreferencesApiClient';
import { verificationApiClient, VerificationStatus } from '../services/verificationApiClient';
import './PrivacyPage.css';
import AETNAInformationModal from '../components/AETNAInformationModal';

interface PrivacyPageProps {
  accessToken?: string;
  refUserId?: number;
}

function PrivacyPage({ accessToken, refUserId }: PrivacyPageProps) {
  const [isAccountInfoModalOpen, setIsAccountInfoModalOpen] = useState(false);
  const [isLoreBotChatActivityModalOpen, setIsLoreBotChatActivityModalOpen] = useState(false);
  const [isLoreBotConsentModalOpen, setIsLoreBotConsentModalOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>({
    isVerified: false,
    partnerDoingBusinessAs: "your business partner",
  });
  const [useLorebotEnabled, setUseLorebotEnabled] = useState(false);
  const [isAccountInformationSharingEnabled, setAccountInformationSharingEnabled] = useState(false);
  const [isLoreBotChatActivitySharingEnabled, setLoreBotChatActivitySharingEnabled] = useState(false);
  const [isAETNARequiredInformationSharingModalOpen, setIsAETNARequiredInformationSharingOpen] = useState(false);
  const [isAETNARequiredInformationSharingEnabled, setIsAETNARequiredInformationSharingEnabled] = useState(false);
  const [isAETNAAuxiliaryInfoModalOpen, setIsAETNAAuxiliaryInfoModalOpen] = useState(false);
  
  // Track whether initial consent status(es) has been loaded from API
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Fetch consent status(es) on component mount
  useEffect(() => {
    const fetchConsents = async () => {
      if (!accessToken || !refUserId) {
        return;
      }

      try {
        const [lorebotConsent, verificationData, accountInfoConsent, chatActivityConsent, aetnaConsent] = await Promise.all([
          userPreferencesApiClient.getLoreBotConsent(refUserId, accessToken),
          verificationApiClient.userVerificationStatus(accessToken),
          userPreferencesApiClient.getAccountInformationSharingConsent(refUserId, accessToken),
          userPreferencesApiClient.getLoreBotChatActivitySharingConsent(refUserId, accessToken),
          userPreferencesApiClient.getAetnaOptionalInformationSharingConsent(refUserId, accessToken),
        ]);

        setUseLorebotEnabled(lorebotConsent);
        setVerificationStatus(verificationData);
        setAccountInformationSharingEnabled(accountInfoConsent);
        setLoreBotChatActivitySharingEnabled(chatActivityConsent);
        setIsAETNARequiredInformationSharingEnabled(aetnaConsent);

        // Explicitly ensure modal(s) are closed when setting state from API
        setIsLoreBotConsentModalOpen(false);
        setIsAETNARequiredInformationSharingOpen(false);

        // Mark initial load as complete after state is set
        setIsInitialLoadComplete(true);
        } catch (error) {
        console.error('Error fetching consent status:', error);
        // On error, leave the toggles in their default state (false)
        setIsLoreBotConsentModalOpen(false);
        setIsAETNARequiredInformationSharingOpen(false);
        setIsInitialLoadComplete(true);
      }
    };

    fetchConsents();
  }, [accessToken, refUserId]);
  
  const handleAccountInfoClick = () => {
    setIsAccountInfoModalOpen(true);
  };

  const handleAccountInfoCloseModal = () => {
    setIsAccountInfoModalOpen(false);
  };

  const handleLoreBotChatInfoClick = () => {
    setIsLoreBotChatActivityModalOpen(true);
  };

  const handleLoreBotChatInfoCloseModal = () => {
    setIsLoreBotChatActivityModalOpen(false);
  };

  const handleLoreBotToggleClick = () => {
    // When toggle is turned on from an "Off" state, open the modal
    setIsLoreBotConsentModalOpen(true);
  };

  const handleLoreBotConsentCloseModal = () => {
    setIsLoreBotConsentModalOpen(false);
  };

  const handleAETNAAuxiliaryInfoOpenModal = () => {
    setIsAETNAAuxiliaryInfoModalOpen(true);
  };

  const handleAETNAAuxiliaryInfoCloseModal = () => {
    setIsAETNAAuxiliaryInfoModalOpen(false);
  };

  const updateLoreBotConsentViaApi = async (consent: boolean) => {
    // Update consent status via API
    if (!accessToken) {
      console.warn('No access token available - cannot update consent');
      return;
    }
    
    if (!refUserId) {
      console.warn('No external ref user ID available - cannot update consent');
      return;
    }
    
    try {
      const response = await userPreferencesApiClient.updateLoreBotConsent(
        refUserId,
        consent,
        accessToken
      );
      console.log(`LoreBot Consent updated successfully for ref_user_id ${refUserId} | should keep on? : ${consent} | Response status: ${response.status}`);
    } catch (error) {
      console.error('Error updating LoreBot Consent:', error);
      // Revert toggle state on error
      setUseLorebotEnabled(!consent);
      throw error;
    }
  };

  const updateAccountInformationSharingConsentViaApi = async (consent: boolean) => {
    if (!accessToken) {
      console.warn('No access token available - cannot update consent');
      return;
    }
    
    if (!refUserId) {
      console.warn('No external ref user ID available - cannot update consent');
      return;
    }
    
    try {
      const response = await userPreferencesApiClient.updateAccountInformationSharingConsent(
        refUserId,
        consent,
        accessToken
      );
      console.log(`Account Information Sharing Consent updated: ${consent} | status: ${response.status}`);
    } catch (error) {
      console.error('Error updating Account Information Sharing Consent:', error);
    }
  };

  const updateLoreBotChatActivitySharingConsentViaApi = async (consent: boolean) => {
    if (!accessToken) {
      console.warn('No access token available - cannot update consent');
      return;
    }
    
    if (!refUserId) {
      console.warn('No external ref user ID available - cannot update consent');
      return;
    }

    try {
      const response = await userPreferencesApiClient.updateLoreBotChatActivitySharingConsent(
        refUserId,
        consent,
        accessToken
      );
      console.log(`LoreBot Chat Activity Sharing Consent updated: ${consent} | status: ${response.status}`);
    } catch (error) {
      console.error('Error updating LoreBot Chat Activity Sharing Consent:', error);
    }
  };

  const updateAetnaOptionalInformationSharingConsentViaApi = async (consent: boolean) => {
    if (!accessToken) {
      console.warn('No access token available - cannot update consent');
      return;
    }
    
    if (!refUserId) {
      console.warn('No external ref user ID available - cannot update consent');
      return;
    }

    try {
      const response = await userPreferencesApiClient.updateAetnaOptionalInformationSharingConsent(
        refUserId,
        consent,
        accessToken
      );
      console.log(`Aetna Optional Information Sharing Consent updated: ${consent} | status: ${response.status}`);
    } catch (error) {
      console.error('Error updating Aetna Optional Information Sharing Consent:', error);
      throw error; // Re-throw to allow rollback in calling function
    }
  };

  const handleLoreBotConsentConfirm = async (shouldKeepOn: boolean) => {
    // Update toggle state based on user's choice
    setUseLorebotEnabled(shouldKeepOn);
    
    // Update consent status via API
    await updateLoreBotConsentViaApi(shouldKeepOn);
  };

  const handleAETNARequiredInformationSharingToggleClick = () => {
    setIsAETNARequiredInformationSharingOpen(true);
  };

  const handleAETNARequiredInformationSharingToggleCloseModal = () => {
    setIsAETNARequiredInformationSharingOpen(false);
  };

  const handleAETNARequiredInformationSharingConfirm = async (shouldKeepOn: boolean) => {
    // Optimistically update toggle state
    setIsAETNARequiredInformationSharingEnabled(shouldKeepOn);
    
    try {
      // Update consent status via API
      await updateAetnaOptionalInformationSharingConsentViaApi(shouldKeepOn);
    } catch (error) {
      // Revert toggle state on error
      setIsAETNARequiredInformationSharingEnabled(!shouldKeepOn);
      throw error;
    }
  };

  // Check if user is verified and partner name contains "AETNA" or "Allina" (case-insensitive)
  const isAETNA = verificationStatus?.isVerified === true && 
    verificationStatus?.partnerDoingBusinessAs && 
    (verificationStatus.partnerDoingBusinessAs.toUpperCase().includes('AETNA') || 
     verificationStatus.partnerDoingBusinessAs.toUpperCase().includes('ALLINA'));

  return (
    <div className="user-preferences-content">
      <div className="page-header-section">
        <h1 className="page-header">Privacy</h1>
        <p className="page-description">Your privacy is important to us. Customize your privacy settings and control your personal information.</p>
      </div>
      <div className="page-content-section">
        {verificationStatus?.isVerified && (
          <div className="account-verification-status-summary">
            Your account is linked to {verificationStatus?.partnerDoingBusinessAs}.
          </div>
        )}
        <div className="configuration-header">Your Consent Choices</div>
        {/* e.g. "Your account is linked to {partner_doing_business_as}. Control what personal information is shared with them."*/}
        <TogglePanel 
          showWarningIcon={true} // TODO: Display warning triangle if directed from LoreBot.
          label="Use LoreBot"
          checked={useLorebotEnabled}
          onChange={async (checked) => {
            if (checked) {
              // Update state
              setUseLorebotEnabled(true);
              // Only open modal if initial load is complete (user interaction) and toggle was off
              if (isInitialLoadComplete && !useLorebotEnabled) {
                // Toggle was off, user is turning it on - open modal
                handleLoreBotToggleClick();
              }
            } else {
              // User is turning toggle off - update state and API
              setUseLorebotEnabled(false);
              // Only update API if initial load is complete (user interaction)
              if (isInitialLoadComplete) {
                await updateLoreBotConsentViaApi(false);
              }
            }
          }}
          />
        {verificationStatus?.isVerified && (
          <div>
            <div className="configuration-header">Data Sharing</div>
            <div className="page-description">
              Your account is linked. Control what personal information is shared with {verificationStatus?.partnerDoingBusinessAs}.
            </div>
            <TogglePanel 
              label="Account Information"
              checked={isAccountInformationSharingEnabled}
              showInfoIcon={true}
              onInfoClick={handleAccountInfoClick}
              onChange={async (checked) => {
                console.log('Toggle sharing of Account Information preference:', checked);
                setAccountInformationSharingEnabled(checked);

                // Only update API if initial load is complete (user interaction)
                if (isInitialLoadComplete) {
                  await updateAccountInformationSharingConsentViaApi(checked);
                }
              }}
            />
            <TogglePanel 
              label="LoreBot Chat Activity"
              checked={isLoreBotChatActivitySharingEnabled}
              showInfoIcon={true}
              onInfoClick={handleLoreBotChatInfoClick}
              onChange={async (checked) => {
                console.log('Toggle sharing of LoreBot Chat Activity preference:', checked);
                setLoreBotChatActivitySharingEnabled(checked);

                // Only update API if initial load is complete (user interaction)
                if (isInitialLoadComplete) {
                  await updateLoreBotChatActivitySharingConsentViaApi(checked);
                }
              }}
            />
          </div>
        )}
        <div className="configuration-header">Other Data Controls</div>
        {/* AETNA is the only partner that requires this toggle to be shown. */}
        {isAETNA && (
          <TogglePanel 
            label="AETNA Information Sharing"
            checked={isAETNARequiredInformationSharingEnabled}
            onChange={async (checked) => {
              if (checked) {
                // Check if toggle is currently off (before state update)
                // This ensures we only open modal when user is turning it on from off
                const isCurrentlyOff = !isAETNARequiredInformationSharingEnabled;
                // Update state optimistically
                setIsAETNARequiredInformationSharingEnabled(true);
                // Only open modal if:
                // 1. Initial load is complete (prevents opening on page refresh)
                // 2. Toggle is currently off (user is turning it on)
                if (isInitialLoadComplete && isCurrentlyOff) {
                  // Toggle was off, user is turning it on - open modal
                  handleAETNARequiredInformationSharingToggleClick();
                }
              } else {
                // User is turning toggle off - update state and API
                setIsAETNARequiredInformationSharingEnabled(false);
                // Only update API if initial load is complete (user interaction)
                if (isInitialLoadComplete) {
                  await updateAetnaOptionalInformationSharingConsentViaApi(false);
                }
              }
            }}
            labelClassName="toggle-panel__label-other-data-controls"
            showInfoIcon={true}
            onInfoClick={handleAETNAAuxiliaryInfoOpenModal}
          />
        )}
        <DeleteButton 
          label="Delete My LoreBot Chats"
          onClick={() => console.log('TODO: Implement LoreBot chat deletion')}
        />
        <DeleteButton 
          label="Delete My Account"
          onClick={() => console.log('TODO: Implement account deletion')}
        />
      </div>
      <AccountInformationModal
        isOpen={isAccountInfoModalOpen}
        onClose={handleAccountInfoCloseModal}
        partnerDoingBusinessAs={verificationStatus?.partnerDoingBusinessAs}
      />
      <LoreBotChatActivityModal
        isOpen={isLoreBotChatActivityModalOpen}
        onClose={handleLoreBotChatInfoCloseModal}
        partnerDoingBusinessAs={verificationStatus?.partnerDoingBusinessAs}
      />
      <LoreBotConsentModal
        isOpen={isLoreBotConsentModalOpen}
        onClose={handleLoreBotConsentCloseModal}
        onConfirm={handleLoreBotConsentConfirm}
      />
      <AETNARequiredInformationSharingConsentModal
        isOpen={isAETNARequiredInformationSharingModalOpen}
        onClose={handleAETNARequiredInformationSharingToggleCloseModal}
        onConfirm={handleAETNARequiredInformationSharingConfirm}
      />
      <AETNAInformationModal
        isOpen={isAETNAAuxiliaryInfoModalOpen}
        onClose={handleAETNAAuxiliaryInfoCloseModal}
      />
    </div>
  );
}

export default PrivacyPage;